// Book desk modules, row 2: the hero equity chart and the holdings heat grid.
// Like the row-1 faces, each is fed by the SAME TanStack query key/options as the
// panel behind its drawer (bookModuleHooks), so opening the drawer is a cache hit,
// and each is state-only: no buttons or links inside.
//
// What a face must never do: read an unknown as a zero, or lose what it already
// has. A dormant book (`bookStatus === null`) is not queried and shows an explicit
// dormant rung; a query that is loading (or paused with no data) shows the
// skeleton; one that failed with nothing to show gets the error rung. The error
// rung is `unavailable(q)`, never a bare `isError`: a background refetch that
// fails keeps the chart or the grid the face already has.
import { useQuery } from '@tanstack/react-query'
import { accountApi } from '../../api/client'
import { useTimeframe } from '../../hooks/useTimeframe'
import { timeframeLabel, toEquityRange } from '../../lib/timeframe'
import type { EquityHistory } from '../../types'
import { BookValueLine } from '../BookValueChart'
import { HeatGrid } from '../viz/HeatGrid'
import { Module } from './Module'
import { useBookPositions, type BookModuleProps } from './bookModuleHooks'
import { heatCells, heatWindow } from './bookSummaries'
import { isDormant, num, stillLoading, unavailable } from './fleetModuleHooks'
import { fmtUsd } from './fleetSummaries'
import './BookModules.css'

// -------------------------------------------------------------- book value

/**
 * The history as chart rows. Equity arrives as a decimal STRING
 * (json_encoders = {Decimal: str}), so Number() is applied here, at the chart
 * boundary, and nowhere earlier. `num` is that Number() with unknowns read as
 * unknown: a null, empty or non-numeric equity is dropped, because a bare
 * Number() would chart it as 0 (null, '') or NaN.
 */
function equityRows(points: EquityHistory['points'] | undefined): Array<{ t: string; equity: number }> {
  if (!Array.isArray(points)) return []
  return points.flatMap((p) => {
    const equity = num(p?.equity)
    return equity === null ? [] : [{ t: p.t, equity }]
  })
}

/**
 * The book's equity line over the global timeframe, filling the module body.
 * Same key and options as BookValueChart (it does not poll: a daily series
 * needs no 30s freshness), so the drawer's chart is a cache hit. DAY charts the
 * 1W range (history is daily), and the caption says so. A dormant book has no
 * history to ask for: it shows the empty rung and fetches nothing, even when an
 * earlier run left rows in the cache.
 */
export function BookValueModule({ book }: BookModuleProps) {
  const { timeframe } = useTimeframe()
  const range = toEquityRange(timeframe)
  const dormant = isDormant(book)
  const q = useQuery({
    queryKey: ['equity-history', book.id, range],
    queryFn: () => accountApi.history(book.id, range),
    enabled: !dormant,
    retry: 1,
    staleTime: 300000,
  })

  const rows = dormant ? [] : equityRows(q.data?.points)

  // A factual text alternative for the chart: the latest equity, how many points are drawn and the
  // window. DAY charts the 1W range, so that is the window it names. No change over the window: the
  // history carries deposits and withdrawals as plain equity, so a percent would be a wrong number.
  const last = rows[rows.length - 1]
  const chartLabel =
    last === undefined
      ? undefined
      : `Book value ${fmtUsd(last.equity)}, ${rows.length} daily points, window ${timeframe === 'DAY' ? '1W' : timeframeLabel(timeframe)}`

  return (
    <Module
      title="Book value"
      caption={timeframe === 'DAY' ? 'showing 1W · day view' : `window ${timeframeLabel(timeframe)}`}
      drawerKey="book-value"
      span={7}
      isLoading={!dormant && stillLoading(q)}
      isError={!dormant && unavailable(q)}
      errorText="chart unavailable"
      isEmpty={rows.length < 2}
      emptyText="no equity history yet"
    >
      <BookValueLine rows={rows} fill ariaLabel={chartLabel} />
    </Module>
  )
}

// ------------------------------------------------------------ holdings heat

/**
 * Every holding as one cell, largest position first, shaded by its day move
 * (HoldingsPanel's key and options: the broker's live positions). Five columns
 * fit a span-5 body (about 95px a cell; a cell needs 78px). Rows share the
 * body's height, so a book over the 25-position cap still shows every name.
 *
 * Unknowns stay unknown. A holding with no day figure (market closed, no
 * lastday price) is a dash with no tone, and the foot says how many. A holding
 * with no market value makes the book's total unknown, and the caption then
 * names no dollar amount: the sum of the rest would read as the whole book. The
 * caption is also left off whenever there is no grid to describe (loading,
 * failed, dormant or empty), because heatCells totals nothing as $0.
 */
export function HoldingsHeatModule({ book }: BookModuleProps) {
  const dormant = isDormant(book)
  const q = useBookPositions(book)
  const { timeframe } = useTimeframe()
  const window = heatWindow(timeframe)
  // The window word on the face: DAY colours by today's move, any longer window by the
  // move since entry (the positions payload has no per-position 1W/1M/3M return).
  const windowText = window === 'day' ? 'day' : 'since entry'
  const figureText = window === 'day' ? 'day P&L' : 'P&L since entry'
  // A dormant book is never read, even from a cache entry an earlier run left behind.
  const { cells, total, unknown, scale } = heatCells(dormant ? undefined : q.data, window)

  let caption: string | undefined
  if (cells.length > 0) {
    const held = total === null ? `${cells.length} held` : `${cells.length} held · ${fmtUsd(total)}`
    caption = `${held} · ${windowText}`
  }

  return (
    <Module
      title="Holdings"
      caption={caption}
      drawerKey="holdings-heat"
      span={5}
      isLoading={!dormant && stillLoading(q)}
      isError={!dormant && unavailable(q)}
      errorText="holdings unavailable"
      isEmpty={cells.length === 0}
      emptyText={dormant ? 'dormant' : 'no open positions'}
    >
      <HeatGrid
        cells={cells}
        columns={5}
        maxAbs={scale}
        ariaLabel={`${cells.length} ${cells.length === 1 ? 'holding' : 'holdings'} by ${figureText}`}
      />
      {unknown > 0 && <p className="module-foot">{`${figureText} unavailable for ${unknown}`}</p>}
    </Module>
  )
}
